import React, { useCallback, useEffect, useRef, useState } from "react";
import CallWorkspace from "./CallWorkspace";
import {
  fetchAssignees,
  fetchBatches,
  fetchCallTypes,
  updateAssignments,
  uploadBatch,
} from "./callWorkspaceApi";
import { formatDate, getToken } from "./callWorkspace.utils";
import { getApiErrorMessage } from "utils/getApiErrorMessage";

const getCurrentDateInputValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const emptyUpload = () => ({ name: "", campaignDate: getCurrentDateInputValue(), file: null, callTypeId: "" });

const CallWorkspaceContainer = ({ history }) => {
  const [filters, setFilters] = useState({ search: "", campaignDate: "" });
  const [appliedFilters, setAppliedFilters] = useState({ search: "", campaignDate: "" });
  const [batches, setBatches] = useState([]);
  const [users, setUsers] = useState([]);
  const [callTypes, setCallTypes] = useState([]);
  const [callTypesError, setCallTypesError] = useState("");
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [upload, setUpload] = useState(emptyUpload);
  const [uploadUsers, setUploadUsers] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [usersError, setUsersError] = useState("");
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const requestId = useRef(0);

  const loadBatches = useCallback((page, activeFilters) => {
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    return fetchBatches(getToken(), {
      search: activeFilters.search || undefined,
      campaign_date: activeFilters.campaignDate || undefined,
      page: page || 1,
      page_size: 15,
    })
      .then((response) => {
        if (requestId.current !== id) return;
        const result = response.data || {};
        setBatches((result.data || []).map((batch) => ({
          ...batch,
          uploader: batch.uploader ? batch.uploader.name : "—",
          uploaded_at: batch.uploaded_at,
        })));
        setPagination({
          current_page: result.current_page || 1,
          last_page: result.last_page || 1,
          total: result.total || 0,
        });
      })
      .catch((apiError) => {
        if (requestId.current === id) setError(getApiErrorMessage(apiError));
      })
      .finally(() => {
        if (requestId.current === id) setLoading(false);
      });
  }, []);

  const loadUsers = useCallback(() => {
    setUsersLoading(true);
    setUsersError("");
    return fetchAssignees(getToken())
      .then((response) => setUsers(response.data || []))
      .catch((apiError) => setUsersError(getApiErrorMessage(apiError)))
      .finally(() => setUsersLoading(false));
  }, []);

  const loadCallTypes = useCallback(() => {
    setCallTypesError("");
    return fetchCallTypes(getToken())
      .then((response) => setCallTypes((response.data || []).filter((item) => item.is_active)))
      .catch((apiError) => setCallTypesError(getApiErrorMessage(apiError)));
  }, []);

  useEffect(() => {
    document.title = "Batch Management | Call Workspace";
    loadBatches(1, appliedFilters);
    loadUsers();
    loadCallTypes();
    return () => {
      requestId.current += 1;
    };
  }, [appliedFilters, loadBatches, loadUsers, loadCallTypes]);

  useEffect(() => {
    if (!showUpload || upload.callTypeId || !callTypes.length) return;
    const defaultType = callTypes.find((type) => type.code === "batch") || callTypes[0];
    setUpload((current) => ({ ...current, callTypeId: String(defaultType.id) }));
  }, [showUpload, upload.callTypeId, callTypes]);
  const changeFilter = (event) =>
    setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));
  const changeUpload = (event) => {
    const { name, value, files } = event.target;
    setUpload((current) => ({ ...current, [name]: name === "file" ? files[0] : value }));
  };
  const toggleUploadUser = (id) =>
    setUploadUsers((current) => current.includes(id)
      ? current.filter((userId) => userId !== id)
      : current.concat(id));
  const closeUpload = () => {
    if (uploading) return;
    setShowUpload(false);
    setUpload(emptyUpload());
    setUploadUsers([]);
    setError("");
  };
  const submitUpload = (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    if (!upload.name.trim() || !upload.campaignDate || !upload.file || !upload.callTypeId || !uploadUsers.length) {
      setError("Batch name, campaign date, call type, file, and at least one caller are required.");
      return;
    }
    if (!callTypes.some((type) => String(type.id) === String(upload.callTypeId))) {
      setError("Select an active call type before uploading a batch.");
      return;
    }
    const payload = new FormData();
    payload.append("name", upload.name.trim());
    payload.append("campaign_date", upload.campaignDate);
    payload.append("call_type_id", upload.callTypeId);
    payload.append("file", upload.file);
    uploadUsers.forEach((id) => payload.append("assignee_ids[]", id));
    setUploading(true);
    uploadBatch(getToken(), payload)
      .then((response) => {
        const result = response.data || {};
        const skipped = Number(result.skipped_blacklisted_count || 0);
        setMessage(
          (response.message || "Batch uploaded successfully.") +
            (skipped ? " " + skipped + " leads marked Don't Call were skipped." : ""),
        );
        setShowUpload(false);
        setUpload(emptyUpload());
        setUploadUsers([]);
        setError("");
        loadBatches(1, appliedFilters);
        loadUsers();
      })
      .catch((apiError) => setError(getApiErrorMessage(apiError)))
      .finally(() => setUploading(false));
  };
  const openAssignment = (batch) => {
    setSelectedBatch(batch);
    setUploadUsers([]);
    setError("");
  };
  const closeAssignment = () => {
    if (assigning) return;
    setSelectedBatch(null);
    setUploadUsers([]);
    setError("");
  };
  const assignBatch = (event) => {
    event.preventDefault();
    if (!selectedBatch || !uploadUsers.length) {
      setError("Select at least one caller.");
      return;
    }
    setAssigning(true);
    setError("");
    updateAssignments(getToken(), {
      batch_id: selectedBatch.id,
      assignee_ids: uploadUsers,
    })
      .then((response) => {
        setMessage(response.message || "Batch leads assigned equally.");
        setSelectedBatch(null);
        setUploadUsers([]);
        loadBatches(pagination.current_page, appliedFilters);
        loadUsers();
      })
      .catch((apiError) => setError(getApiErrorMessage(apiError)))
      .finally(() => setAssigning(false));
  };

  return (
    <CallWorkspace
      onManageCallTypes={() => history.push("/admin/call-workspace/call-types")}
      batches={batches}
      users={users}
      callTypes={callTypes}
      callTypesError={callTypesError}
      batchCallType={callTypes.find((type) => type.code === "batch") || null}
      onRetryCallTypes={loadCallTypes}
      filters={filters}
      selectedBatch={selectedBatch}
      showUpload={showUpload}
      upload={upload}
      uploadUsers={uploadUsers}
      message={message}
      error={error}
      usersError={usersError}
      loading={loading}
      usersLoading={usersLoading}
      uploading={uploading}
      assigning={assigning}
      pagination={pagination}
      onFilterChange={changeFilter}
      onApplyFilters={(event) => { event.preventDefault(); setAppliedFilters(filters); }}
      onClearFilters={() => {
        const cleared = { search: "", campaignDate: "" };
        setFilters(cleared);
        setAppliedFilters(cleared);
      }}
      onPageChange={(page) => loadBatches(page, appliedFilters)}
      onSelectBatch={openAssignment}
      onViewBatch={(batch) => history.push("/admin/call-workspace/batches/" + batch.id)}
      onAssignBatch={assignBatch}
      onCloseAssignment={closeAssignment}
      onShowUpload={() => {
        setMessage("");
        setError("");
        setUpload(emptyUpload());
        setUploadUsers([]);
        loadCallTypes();
        setShowUpload(true);
      }}
      onHideUpload={closeUpload}
      onUploadChange={changeUpload}
      onUpload={submitUpload}
      onToggleUploadUser={toggleUploadUser}
      onDismissMessage={() => setMessage("")}
      onRetryUsers={loadUsers}
      formatDate={formatDate}
    />
  );
};

export default CallWorkspaceContainer;
